import type { TreeSelectProps } from 'antd';
import { TreeSelect, Typography } from 'antd';
import type { CarrierSummaryServiceMethodSummary } from '~/store/services/CarrierService/types';
import { usePartnerCarrierSummaries } from '~/hooks/queries/usePartnerCarrierSummaries';

const { Text } = Typography;
interface CarrierServiceMethodSelectProps extends TreeSelectProps {
  filterNonConfiguredServiceMethods?: boolean;
  partnerId?: string;
}

export const CarrierServiceMethodSelect = ({
  partnerId,
  filterNonConfiguredServiceMethods = false,
  ...props
}: CarrierServiceMethodSelectProps) => {
  const { data: partnerCarrierSummaries, isLoading, isError } = usePartnerCarrierSummaries({ partnerId });
  const failureReason = (partnerCarrierSummaries as any)?.failureReason?.message;
  const optionalServiceMethodFilter = (serviceMethods?: CarrierSummaryServiceMethodSummary[]) => {
    return (
      serviceMethods?.filter((serviceMethod) =>
        filterNonConfiguredServiceMethods ? serviceMethod.configured : true,
      ) || []
    );
  };
  const carrierServiceMethodOptions = Array.isArray(partnerCarrierSummaries)
    ? partnerCarrierSummaries
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .filter(
          (val) =>
            // Some partners have setup carriers with no configured service methods
            optionalServiceMethodFilter(val.serviceMethods).length > 0,
        )
        .map((val) => {
          return {
            title: val.name,
            value: val.carrierId,
            key: val.carrierId,
            children: optionalServiceMethodFilter(val.serviceMethods)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((serviceMethod) => {
                return {
                  title: serviceMethod.name,
                  value: serviceMethod.serviceMethodId,
                  key: serviceMethod.serviceMethodId,
                };
              }),
          };
        })
    : [];

  return (
    <>
      <TreeSelect
        loading={isLoading}
        disabled={isLoading || isError}
        treeCheckable
        showCheckedStrategy="SHOW_CHILD"
        allowClear
        showSearch
        listHeight={512}
        treeData={carrierServiceMethodOptions}
        filterTreeNode={(input, option) => {
          return option.title?.toString().toLowerCase().includes(input.toLowerCase()) || false;
        }}
        {...props}
      />
      {isError && (
        <Text type="danger" style={{ display: 'block' }}>
          Error loading carriers {failureReason ? ` (${failureReason})` : ''}
        </Text>
      )}
    </>
  );
};
